/* useIsMobile — the app's single JS breakpoint (matches the ≤768px CSS
   breakpoint used across component style blocks). Consume this only where
   the DOM or behavior must differ between phone and desktop — drawer nav,
   panel presentation, autofocus/Enter-send gating. Pure styling
   differences belong in each component's embedded <style> block via
   media queries, not here. */

import { useSyncExternalStore } from 'react';

export const MOBILE_QUERY = '(max-width: 768px)';

/* matchMedia is absent in the node vitest environment (no window at all)
   and under bare jsdom — both must fall back to desktop behavior, so the
   window reference itself has to be guarded, not just .matchMedia. */
function canMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function subscribe(onChange: () => void): () => void {
  if (!canMatchMedia()) return () => {};
  const mql = window.matchMedia(MOBILE_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getSnapshot(): boolean {
  return canMatchMedia() && window.matchMedia(MOBILE_QUERY).matches;
}

export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** One-shot read for non-hook contexts (initial state, event handlers). */
export function isMobileViewport(): boolean {
  return getSnapshot();
}

/** True on touch-first devices. Use to gate desktop-only interactions
    (Enter-to-send, autoFocus) rather than viewport width — an attached
    keyboard on a small window should still behave like a desktop. */
export function isCoarsePointer(): boolean {
  return canMatchMedia() && window.matchMedia('(pointer: coarse)').matches;
}
